/**
 * FE-T04 / A-T02 — SubDelegatesModal (redesigned)
 *
 * Admin-facing dialog to manage the sub-delegates of any non-`SUB_DELEGATE`
 * owner: list active + pending, invite, resend, cancel, revoke, and edit
 * permissions. Built on the shared shadcn `Dialog` and the shared
 * `sub-delegation` primitives (badges, seat pill, toggle cards).
 *
 * Split into:
 *   - `InviteSubDelegateForm.tsx` — collapsible invite form
 *   - `SubDelegateListItem.tsx`   — one row (active or pending)
 */

import { useState } from 'react';
import { AlertCircle, Users } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Separator } from '@/shared/components/ui/separator';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { useToast } from '@/shared/components/Toast';
import type { AdminUser } from '../../types/admin-user.type';
import {
  useAdminSubDelegates,
  useCancelAdminSubDelegate,
  useCreateAdminSubDelegate,
  useResendAdminSubDelegate,
  useRevokeAdminSubDelegate,
  useUpdateAdminSubDelegatePermissions,
} from '../../hooks/useAdminSubDelegates';
import { InviteSubDelegateForm } from './InviteSubDelegateForm';
import { SeatLimitControl } from './SeatLimitControl';
import { SubDelegateListItem } from './SubDelegateListItem';
import type { CreateSubDelegatePayload } from '../../types/sub-delegate.type';

type SubDelegatesModalProps = {
  owner: AdminUser;
  onClose: () => void;
};

const ownerDisplayName = (owner: AdminUser): string =>
  owner.name ||
  [owner.firstName, owner.lastName].filter(Boolean).join(' ') ||
  owner.email;

const errorMessageFrom = (error: unknown): string | null => {
  if (!error) return null;
  if (typeof error === 'string') return error;
  if (typeof error === 'object' && 'message' in (error as object)) {
    const msg = (error as { message?: unknown }).message;
    if (typeof msg === 'string') return msg;
  }
  return 'Something went wrong. Please try again.';
};

export function SubDelegatesModal({ owner, onClose }: SubDelegatesModalProps) {
  const ownerName = ownerDisplayName(owner);
  const { showToast } = useToast();

  const list = useAdminSubDelegates(owner.id);
  const createDelegate = useCreateAdminSubDelegate(owner.id);
  const resendDelegate = useResendAdminSubDelegate(owner.id);
  const cancelDelegate = useCancelAdminSubDelegate(owner.id);
  const revokeDelegate = useRevokeAdminSubDelegate(owner.id);
  const updatePermissions = useUpdateAdminSubDelegatePermissions(owner.id);

  const [inviteError, setInviteError] = useState<string | null>(null);

  const handleInvite = async (payload: CreateSubDelegatePayload) => {
    setInviteError(null);
    try {
      await createDelegate.mutateAsync(payload);
      showToast(`Invitation sent to ${payload.email}`, 'success');
    } catch (error) {
      setInviteError(errorMessageFrom(error));
    }
  };

  const mutateWithToast = (
    action: Promise<unknown>,
    successMessage: string,
  ) => {
    action
      .then(() => showToast(successMessage, 'success'))
      .catch((error) =>
        showToast(errorMessageFrom(error) ?? 'Action failed', 'error'),
      );
  };

  const isAnyMutating =
    resendDelegate.isPending ||
    cancelDelegate.isPending ||
    revokeDelegate.isPending;

  const data = list.data;
  const usedCount = (data?.meta.active ?? 0) + (data?.meta.pending ?? 0);
  const effectiveLimit = data?.meta.effectiveLimit ?? 3;
  const atLimit = !!data && usedCount >= effectiveLimit;

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        className="flex h-[85vh] max-h-[720px] w-full max-w-2xl flex-col gap-0 overflow-hidden p-0"
        aria-label={`Manage sub-delegates for ${ownerName}`}
      >
        {/* Header */}
        <DialogHeader className="border-b border-gray-100 px-6 py-4">
          <div className="flex items-center gap-3 pr-8">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#026857]/10 text-[#026857]">
              <Users className="h-5 w-5" />
            </span>
            <div className="min-w-0 flex-1">
              <DialogTitle className="truncate text-base">
                Sub-delegates for {ownerName}
              </DialogTitle>
              <p className="mt-0.5 text-xs text-gray-500">
                {/* Kept for test/back-compat: "X of Y used" */}
                {usedCount} of {effectiveLimit} used
              </p>
            </div>
            {data && (
              <SeatLimitControl
                ownerId={owner.id}
                ownerName={ownerName}
                limit={data.meta.limit}
                effectiveLimit={effectiveLimit}
                usedCount={usedCount}
                errorMessageFrom={errorMessageFrom}
              />
            )}
          </div>
        </DialogHeader>

        {/* Body */}
        <div className="flex-1 space-y-5 overflow-auto px-6 py-5">
          {list.isLoading && (
            <div className="space-y-3" data-testid="loading-state">
              <Skeleton className="h-12 w-full rounded-lg" />
              <Skeleton className="h-16 w-full rounded-lg" />
              <Skeleton className="h-16 w-full rounded-lg" />
            </div>
          )}

          {list.isError && (
            <div
              className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
              role="alert"
              data-testid="error-state"
            >
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{errorMessageFrom(list.error) ?? 'Failed to load.'}</span>
            </div>
          )}

          {data && (
            <>
              <InviteSubDelegateForm
                isSubmitting={createDelegate.isPending}
                errorMessage={inviteError}
                disabled={atLimit}
                onSubmit={handleInvite}
              />

              <section aria-label="Active sub-delegates" className="space-y-2">
                <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Active ({data.active.length})
                </h4>
                {data.active.length === 0 ? (
                  <p className="text-sm text-gray-500" data-testid="empty-active">
                    No active sub-delegates yet.
                  </p>
                ) : (
                  <ul className="space-y-2">
                    {data.active.map((row) => (
                      <SubDelegateListItem
                        key={row.id}
                        kind="active"
                        item={row}
                        isMutating={revokeDelegate.isPending}
                        isSavingPermissions={updatePermissions.isPending}
                        onRevoke={(id) =>
                          mutateWithToast(
                            revokeDelegate.mutateAsync(id),
                            'Access revoked',
                          )
                        }
                        onUpdatePermissions={(id, permissions) =>
                          mutateWithToast(
                            updatePermissions.mutateAsync({
                              relationshipId: id,
                              permissions,
                            }),
                            'Permissions updated',
                          )
                        }
                      />
                    ))}
                  </ul>
                )}
              </section>

              <Separator />

              <section aria-label="Pending invitations" className="space-y-2">
                <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Pending ({data.pending.length})
                </h4>
                {data.pending.length === 0 ? (
                  <p className="text-sm text-gray-500" data-testid="empty-pending">
                    No pending invitations.
                  </p>
                ) : (
                  <ul className="space-y-2">
                    {data.pending.map((row) => (
                      <SubDelegateListItem
                        key={row.id}
                        kind="pending"
                        item={row}
                        isMutating={isAnyMutating}
                        onResend={(id) =>
                          mutateWithToast(
                            resendDelegate.mutateAsync(id),
                            'Invitation resent',
                          )
                        }
                        onCancel={(id) =>
                          mutateWithToast(
                            cancelDelegate.mutateAsync(id),
                            'Invitation cancelled',
                          )
                        }
                      />
                    ))}
                  </ul>
                )}
              </section>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end border-t border-gray-100 px-6 py-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-gray-300 px-4 py-2 text-sm text-gray-700 transition-colors hover:bg-gray-50"
          >
            Close
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
